import _ from 'lodash'
// Utils
import {logDataByLevel} from '../utils/logging-helpers'
import {getValidParamValue, deepTrim} from '../utils/string-helpers'
// Consts
import {LOCALHOST_TEST_WEBSITE} from '../utils/ballot-helpers'
import {CHECKOUT_TIMEOUT_MS, SUBMISSION_TIMEOUT_MS} from '../consts/payments'
// Types
import type {Ballot} from '../types/ballot'
import type {Customer} from '../types/customer'
import type {Voter, Vote} from '../types/voter'
import type {Website} from '../types/website'

// ============================================================================
// API REQUEST/RESPONSE TYPE DEFINITIONS
// ============================================================================

export interface ApiError extends Error {
  api: {
    status: number
    statusText: string
    url: string
    requestBody: any
    responseBody: string | null
  }
}

export interface FindWebsiteRequest {
  website: string
}

export interface ValidateUrlPathRequest {
  url_path?: string
  customer_id?: string
  ballot_id?: string
  [key: string]: any
}

export interface ValidateUrlPathResponse {
  isValid: boolean
  message?: string
  [key: string]: any
}

export interface AddVoterRequest {
  ballot_id: string
  email: string
  first_name?: string
  last_name?: string
  ip?: string
  ip_country?: string
  ip_region?: string
  finger_print_hash?: string
  geo_info?: {
    zip_code?: string
    [key: string]: any
  }
  [key: string]: any
}

export interface AddVoteRequest {
  voter_id: string
  ballot_id: string
  category_id: string
  sub_category_id: string
  voted_choice: string
  email?: string
  name?: string
  comment?: string
  photo_name?: string
  photo_description?: string
  [key: string]: any
}

export interface FindBallotIdRequest {
  orgSlug: string
  ballotSlug: string
}

export interface FindBallotIdResponse {
  ballot_id: string
}

export interface AddAnalyticRequest {
  ballot_id?: string
  voter_id?: string
  event_type?: string
  [key: string]: any
}

export interface CheckOutWithStripeRequest {
  amount: number
  currency?: string
  description?: string
  [key: string]: any
}

export interface CheckOutWithStripeResponse {
  sessionId?: string
  url?: string
  [key: string]: any
}

export interface FindHasRegisteredRequest {
  email: string
}

export interface FindHasRegisteredResponse {
  success: boolean
  errorMessage?: string
  value?: {
    exists: boolean
  }
}

export interface FindPreviousVoterRequest {
  ballot_id: string
  email: string
}

// ============================================================================
// ERROR HANDLING
// ============================================================================

const handleApiError = async (
  response: Response,
  fetchUrl: string,
  requestBody: any = null,
  elapsed: number | null = null,
): Promise<never> => {
  let errorMessage = `API Error: ${response.status} ${response.statusText} -- ${fetchUrl}`
  if (elapsed !== null) errorMessage += `\nElapsed time: ${elapsed}ms`
  let responseBody: string | null = null
  try {
    responseBody = await response.text()
    if (responseBody) errorMessage += `\nResponse body: ${responseBody}`
  } catch {
    // Ignore parsing errors
  }
  if (requestBody) errorMessage += `\nRequest body: ${JSON.stringify(requestBody)}`
  const errorObject = new Error(errorMessage) as ApiError

  errorObject.api = {
    status: response.status,
    statusText: response.statusText,
    url: fetchUrl,
    requestBody,
    responseBody,
  }
  logDataByLevel({
    level: 'error',
    message: errorMessage,
    data: errorObject,
  })
  throw errorObject
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

export async function findWebsite(
  requestHost: string | null = null,
  apitoken: string | null = null,
): Promise<Website | null> {
  const startTime = Date.now()
  try {
    if (requestHost === 'localhost:3100') return LOCALHOST_TEST_WEBSITE

    const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/websites/findWebsite`
    const requestBody = {website: requestHost}
    const headers: Record<string, string> = {'Content-Type': 'application/json'}
    if (apitoken) headers['apitoken'] = apitoken
    const requestOptions = {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    }

    const response = await fetch(fetchUrl, requestOptions)
    const elapsed = Date.now() - startTime
    if (!response.ok) await handleApiError(response, fetchUrl, requestBody, elapsed)

    const foundWebsites = await response.json()
    return _.first(foundWebsites)
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    logDataByLevel({
      level: 'error',
      message: `${errorMessage}: findWebsite`,
      data: error,
    })
    throw error
  }
}

export async function getWebsiteByBallotId(
  ballotId: string,
  apitoken: string | null = null,
): Promise<Website | null> {
  if (!ballotId) return null
  const startTime = Date.now()
  try {
    const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/websites/getWebsiteByBallotId/${ballotId}`
    const requestOptions: RequestInit = {
      method: 'GET',
      ...(apitoken && {headers: {apitoken}}),
    }

    const response = await fetch(fetchUrl, requestOptions)
    const elapsed = Date.now() - startTime
    if (!response.ok) await handleApiError(response, fetchUrl, null, elapsed)

    const data = await response.json()
    return _.first(data)
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    logDataByLevel({
      level: 'error',
      message: `${errorMessage}: getWebsiteByBallotId`,
      data: error,
    })
    throw error
  }
}

export async function validateUrlPath(
  requestBody: ValidateUrlPathRequest,
  apitoken: string | null = null,
): Promise<ValidateUrlPathResponse | null> {
  if (!requestBody) return null

  const trimmedRequestBody = deepTrim(requestBody)
  const startTime = Date.now()
  try {
    const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/websites/validateUrlPath`
    const headers: Record<string, string> = {'Content-Type': 'application/json'}
    if (apitoken) headers['apitoken'] = apitoken
    const requestOptions = {
      method: 'POST',
      headers,
      body: JSON.stringify(trimmedRequestBody),
    }

    const response = await fetch(fetchUrl, requestOptions)
    const elapsed = Date.now() - startTime
    if (!response.ok) await handleApiError(response, fetchUrl, trimmedRequestBody, elapsed)

    const data = await response.json()
    return data
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    logDataByLevel({
      level: 'error',
      message: `${errorMessage}: validateUrlPath`,
      data: error,
    })
    throw error
  }
}

export async function getCustomerById(
  customerId: string,
  apitoken: string | null = null,
): Promise<Customer | null> {
  if (!customerId) return null
  const startTime = Date.now()
  try {
    const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/customers/${customerId}`
    const requestOptions: RequestInit = {
      method: 'GET',
      ...(apitoken && {headers: {apitoken}}),
    }

    const response = await fetch(fetchUrl, requestOptions)
    const elapsed = Date.now() - startTime
    if (!response.ok) await handleApiError(response, fetchUrl, null, elapsed)

    const data = await response.json()
    return data
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    logDataByLevel({
      level: 'error',
      message: `${errorMessage}: getCustomerById`,
      data: error,
    })
    throw error
  }
}

export async function getCustomerBasicById(
  customerId: string,
): Promise<Customer | null> {
  if (!customerId) return null
  const startTime = Date.now()
  try {
    const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/customers/getCustomerBasicById/${customerId}`
    const requestOptions = {method: 'GET'}

    const response = await fetch(fetchUrl, requestOptions)
    const elapsed = Date.now() - startTime
    if (!response.ok) await handleApiError(response, fetchUrl, null, elapsed)

    // Odd that this returns an array, but takes a unique id
    const customersArray = await response.json()
    return _.first(customersArray)
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    logDataByLevel({
      level: 'error',
      message: `${errorMessage}: getCustomerBasicById`,
      data: error,
    })
    throw error
  }
}

export async function getBallotBasicById(
  ballotId: string,
  apitoken: string | null = null,
): Promise<Ballot | null> {
  if (!ballotId) return null
  const startTime = Date.now()
  try {
    const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/ballots/getBallotBasicById/${ballotId}`
    const requestOptions: RequestInit = {
      method: 'GET',
      ...(apitoken && {headers: {apitoken}}),
    }

    const response = await fetch(fetchUrl, requestOptions)
    const elapsed = Date.now() - startTime
    if (!response.ok) await handleApiError(response, fetchUrl, null, elapsed)

    const data = await response.json()
    return data
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    logDataByLevel({
      level: 'error',
      message: `${errorMessage}: getBallotBasicById`,
      data: error,
    })
    throw error
  }
}

export async function getBallotById(
  ballotId: string,
  apitoken: string | null = null,
): Promise<Ballot | null> {
  if (!ballotId) return null
  const startTime = Date.now()
  try {
    const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/ballots/${ballotId}`
    const requestOptions: RequestInit = {
      method: 'GET',
      ...(apitoken && {headers: {apitoken}}),
    }

    const response = await fetch(fetchUrl, requestOptions)
    const elapsed = Date.now() - startTime
    if (!response.ok) await handleApiError(response, fetchUrl, null, elapsed)

    const data = await response.json()
    return data
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    logDataByLevel({
      level: 'error',
      message: `${errorMessage}: getBallotById`,
      data: error,
    })
    throw error
  }
}

export async function findPreviousVoterByBallotIdAndEmail(
  ballotId: string,
  emailAddress: string,
): Promise<Voter | null> {
  const trimmedBallotId = _.trim(ballotId)
  const trimmedEmail = _.trim(emailAddress)
  if (_.isEmpty(trimmedBallotId) || _.isEmpty(trimmedEmail)) return null
  const startTime = Date.now()
  try {
    const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/voters/findPreviousVoterByBallotByEmail/`
    const requestBody = deepTrim({
      ballot_id: trimmedBallotId,
      email: trimmedEmail,
    })
    const requestOptions = {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(requestBody),
    }

    const response = await fetch(fetchUrl, requestOptions)
    const elapsed = Date.now() - startTime
    if (!response.ok) await handleApiError(response, fetchUrl, requestBody, elapsed)

    const data = await response.json()
    // This api call returns an empty array if no voter is found
    // If that's the case, we return null to avoid returning an empty array in the UI
    return (Array.isArray(data) && data.length === 0) ? null : data
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    logDataByLevel({
      level: 'error',
      message: `${errorMessage}: findPreviousVoterByBallotIdAndEmail`,
      data: error,
    })
    throw error
  }
}

export async function getVoterById(
  voterId: string,
): Promise<Voter | null> {
  if (!getValidParamValue(voterId)) return null
  const startTime = Date.now()
  try {
    const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/voters/getVoterById/${voterId}`
    const requestOptions = {method: 'GET'}

    const response = await fetch(fetchUrl, requestOptions)
    const elapsed = Date.now() - startTime
    if (!response.ok) await handleApiError(response, fetchUrl, null, elapsed)

    const data = await response.json()
    return data
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    logDataByLevel({
      level: 'error',
      message: `${errorMessage}: getVoterById`,
      data: error,
    })
    throw error
  }
}

export async function addVoter(
  requestBody: AddVoterRequest,
): Promise<Voter | null> {
  if (_.isEmpty(requestBody)) return null
  const trimmedRequestBody = deepTrim(requestBody)
  const startTime = Date.now()
  try {
    const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/voters/add`
    const requestOptions = {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(trimmedRequestBody),
    }

    const response = await fetch(fetchUrl, requestOptions)
    const elapsed = Date.now() - startTime
    if (!response.ok) await handleApiError(response, fetchUrl, trimmedRequestBody, elapsed)

    const data = await response.json()
    return data
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    logDataByLevel({
      level: 'error',
      message: `${errorMessage}: addVoter`,
      data: error,
    })
    throw error
  }
}

export async function getVotesByBallotIdAndVoterId(
  ballotId: string,
  voterId: string,
): Promise<Vote[] | null> {
  if (!ballotId || !voterId) return null
  const startTime = Date.now()
  try {
    const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/votes/getVotesByBallotByVoter/${ballotId}/${voterId}`
    const requestOptions = {method: 'GET'}

    const response = await fetch(fetchUrl, requestOptions)
    const elapsed = Date.now() - startTime
    if (!response.ok) await handleApiError(response, fetchUrl, null, elapsed)

    const data = await response.json()
    return data
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    logDataByLevel({
      level: 'error',
      message: `${errorMessage}: getVotesByBallotIdAndVoterId`,
      data: error,
    })
    throw error
  }
}

export async function addVote(
  requestBody: AddVoteRequest,
): Promise<Vote | null> {
  if (!requestBody) return null
  const trimmedRequestBody = deepTrim(requestBody)
  const startTime = Date.now()
  // Bound the persist call so a hung backend can't leave submissionIsProcessing
  // stuck true (blocking wizard close / hanging the paid-flow castSubmission).
  const abortController = new AbortController()
  const timeoutId = setTimeout(() => abortController.abort(), SUBMISSION_TIMEOUT_MS)
  try {
    const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/votes/add`
    const requestOptions = {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(trimmedRequestBody),
      signal: abortController.signal,
    }

    const response = await fetch(fetchUrl, requestOptions)
    const elapsed = Date.now() - startTime
    if (!response.ok) await handleApiError(response, fetchUrl, trimmedRequestBody, elapsed)

    const data = await response.json()
    return data
  } catch (error) {
    const elapsed = Date.now() - startTime
    const errorMessage = abortController.signal.aborted
      ? `addVote timed out after ${elapsed}ms`
      : (error instanceof Error ? error.message : 'Unknown error')
    logDataByLevel({
      level: 'error',
      message: `${errorMessage}: addVote`,
      data: error,
    })
    throw error
  } finally {
    clearTimeout(timeoutId)
  }
}

export async function findBallotIdByOrgSlugAndBallotSlug(
  orgSlug: string,
  ballotSlug: string,
  apitoken: string | null = null,
): Promise<string | null> {
  const sanitizedOrgSlug = _.trim(orgSlug)
  const sanitizedBallotSlug = _.trim(ballotSlug)

  if (!sanitizedOrgSlug || !sanitizedBallotSlug) return null
  const startTime = Date.now()
  try {
    const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/customers/findBallotIdByOrgSlugAndBallotSlug`
    const requestBody = {
      orgSlug: sanitizedOrgSlug,
      ballotSlug: sanitizedBallotSlug,
    }
    const requestOptions: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(apitoken && {apitoken}),
      },
      body: JSON.stringify(requestBody),
    }

    const response = await fetch(fetchUrl, requestOptions)
    const elapsed = Date.now() - startTime
    if (!response.ok) await handleApiError(response, fetchUrl, requestBody, elapsed)

    const contentType = response.headers.get('content-type')?.toLowerCase() || ''
    const responseBody = await response.text()

    if (!responseBody) return null

    const truncatedResponseBody = _.truncate(responseBody, {length: 1000})

    if (!contentType.includes('application/json')) {
      throw new Error(
        `Expected JSON response but received ${contentType || 'unknown content-type'}. Response body (truncated): ${truncatedResponseBody}`,
      )
    }

    let data: FindBallotIdResponse
    try {
      data = JSON.parse(responseBody) as FindBallotIdResponse
    } catch {
      throw new Error(
        `Invalid JSON response from findBallotIdByOrgSlugAndBallotSlug. Response body (truncated): ${truncatedResponseBody}`,
      )
    }

    return data.ballot_id || null
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    logDataByLevel({
      level: 'error',
      message: `${errorMessage}: findBallotIdByOrgSlugAndBallotSlug`,
      data: error,
    })
    throw error
  }
}

export async function addAnalytic(
  requestBody: AddAnalyticRequest,
): Promise<any | null> {
  if (_.isEmpty(requestBody)) return null
  const startTime = Date.now()
  try {
    const fetchUrl = `${process.env.NEXT_PUBLIC_BEST_OF_ANALYTICS_HOST}/insert`
    const requestOptions = {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(requestBody),
    }

    const response = await fetch(fetchUrl, requestOptions)
    const elapsed = Date.now() - startTime
    if (!response.ok) await handleApiError(response, fetchUrl, requestBody, elapsed)

    const data = await response.json()
    return data
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    logDataByLevel({
      level: 'error',
      message: `${errorMessage}: addAnalytic`,
      data: error,
    })
    throw error
  }
}

export async function checkOutWithStripe(
  requestBody: CheckOutWithStripeRequest,
  externalSignal?: AbortSignal,
): Promise<CheckOutWithStripeResponse | null> {
  if (_.isEmpty(requestBody)) return null
  const trimmedRequestBody = deepTrim(requestBody)
  const startTime = Date.now()
  // When the caller supplies a signal it owns cancellation/timeout (e.g. the
  // pay flow aborts on its UI timeout). Otherwise fall back to an internal
  // timeout so a hung backend still aborts on its own.
  const internalController = externalSignal ? null : new AbortController()
  const timeoutId = internalController
    ? setTimeout(() => internalController.abort(), CHECKOUT_TIMEOUT_MS)
    : null
  const signal = externalSignal ?? internalController!.signal
  try {
    const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/payments/checkOut`
    const requestOptions = {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(trimmedRequestBody),
      signal,
    }

    const response = await fetch(fetchUrl, requestOptions)
    const elapsed = Date.now() - startTime
    if (!response.ok) await handleApiError(response, fetchUrl, trimmedRequestBody, elapsed)

    const data = await response.json()
    return data
  } catch (error) {
    const elapsed = Date.now() - startTime
    const errorMessage = signal.aborted
      ? `Payment request timed out/aborted after ${elapsed}ms`
      : (error instanceof Error ? error.message : 'Unknown error')
    logDataByLevel({
      level: 'error',
      message: `${errorMessage}: checkOutWithStripe`,
      data: error,
    })
    throw error
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}

export async function findHasRegisteredForCitysparkAccountByEmail(
  email: string | null = null,
): Promise<boolean | null> {
  const trimmedEmail = _.trim(email ?? '')
  if (_.isEmpty(trimmedEmail)) return null
  const requestBody = {email: trimmedEmail}
  const startTime = Date.now()
  try {
    const fetchUrl = `${process.env.NEXT_PUBLIC_CITYSPARK_API_HOST}/api/user/exists`
    const requestOptions = {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(requestBody),
    }

    const response = await fetch(fetchUrl, requestOptions)
    const elapsed = Date.now() - startTime
    if (!response.ok) await handleApiError(response, fetchUrl, requestBody, elapsed)

    const data: FindHasRegisteredResponse = await response.json()
    if (!data?.success) {
      const errorMsg = data?.errorMessage ? data.errorMessage : 'Unknown error'
      logDataByLevel({
        level: 'error',
        message: `${errorMsg} -- email: ${trimmedEmail}`,
        data: {email: trimmedEmail, responseData: data},
      })
      throw new Error(errorMsg)
    }
    return data?.value?.exists || false
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    logDataByLevel({
      level: 'error',
      message: `${errorMessage}: findHasRegisteredForCitysparkAccountByEmail`,
      data: error,
    })
    throw error
  }
}

// export async function addPayment(
//   requestBody,
// ) {
//   logDataByLevel({
//     level: 'info',
//     message: 'addPayment info',
//     data: {
//       adInfo: requestBody?.ad_info,
//       paymentGatewayInfo: requestBody?.payment_gateway_info,
//     },
//   })
//   if (_.isEmpty(requestBody)) return null
//   const startTime = Date.now()
//   try {
//     const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/payments/add`
//     const requestOptions = {
//       method: 'POST',
//       headers: {'Content-Type': 'application/json'},
//       body: JSON.stringify(requestBody),
//     }

//     const response = await fetch(fetchUrl, requestOptions)
//     const elapsed = Date.now() - startTime
//     if (!response.ok) await handleApiError(response, fetchUrl, requestBody, elapsed)

//     const data = await response.json()
//     return data
// //   } catch (error) {
// logDataByLevel({
//   level: 'error',
//   message: `${error.message}: addPayment`,
//   data: error,
// })
//     throw error
//   }
// }

// export async function sendPurchaseEmail(
//   requestBody,
// ) {
//   if (_.isEmpty(requestBody)) return null
//   const startTime = Date.now()
//   try {
//     let fetchUrl = null
//     switch (requestBody.adType) {
//     case 'Category display ad':
//     case 'Category sponsored write-in':
//     case 'Category profile card':
//     case 'Category profile card with display ad':
//       fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/ballots/sendEmailAfterAddingCategoryAd`
//       break
//     case 'Group sponsorship ad':
//     case 'Group interstitial ad':
//       fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/ballots/sendEmailAfterAddingGroupAd`
//       break
//     case 'Ad package':
//       fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/ballots/sendEmailAfterAddingAdPackage`
//       break
//     case 'GeneralAd':
//       fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/ballots/sendEmailAfterAddingGeneralAd`
//       break
//     case 'Custom voting link ad':
//       fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/ballots/sendEmailAfterAddingCustomVotingLink`
//       break
//     default:
//       fetchUrl = null
//     }
//     if (!fetchUrl)
//       throw new Error('Error --', fetchUrl)

//     const requestOptions = {
//       method: 'POST',
//       headers: new Headers({'Content-Type': 'application/json'}),
//       body: JSON.stringify(requestBody),
//     }

//     const response = await fetch(fetchUrl, requestOptions)
//     const elapsed = Date.now() - startTime
//     if (!response.ok) await handleApiError(response, fetchUrl, requestBody, elapsed)

//     const data = await response.json()
//     return data
// //   } catch (error) {
// logDataByLevel({
//   level: 'error',
//   message: `${error.message}: sendPurchaseEmail`,
//   data: error,
// })
//     throw error
//   }
// }

// export async function addMultiplePrechoices(
//   requestBody,
// ) {
//   if (_.isEmpty(requestBody)) return null
//   const startTime = Date.now()
//   try {
//     const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/ballots/addMultiplePrechoices`
//     const requestOptions = {
//       method: 'POST',
//       headers: {'Content-Type': 'application/json'},
//       body: JSON.stringify(requestBody),
//     }

//     const response = await fetch(fetchUrl, requestOptions)
//     const elapsed = Date.now() - startTime
//     if (!response.ok) await handleApiError(response, fetchUrl, requestBody, elapsed)

//     const data = await response.json()
//     return data
// //   } catch (error) {
// logDataByLevel({
//   level: 'error',
//   message: `${error.message}: addMultiplePrechoices`,
//   data: error,
// })
//     throw error
//   }
// }

// export async function searchRegisteredBusinesses(
//   registeredBusinessSearchBody = null,
//   ballotId = null,
// ) {
//   if (
//     !registeredBusinessSearchBody
//     || !registeredBusinessSearchBody.q
//   ) return null
//   const startTime = Date.now()
//   try {
//     const fetchUrl = `${process.env.NEXT_PUBLIC_CITYSPARK_API_HOST}/api/business/search`
//     let requestBody = _.cloneDeep(registeredBusinessSearchBody)

//     if (ballotId) requestBody['ballotId'] = ballotId
//     const requestOptions = {
//       method: 'POST',
//       headers: {'Content-Type': 'application/json'},
//       body: JSON.stringify(requestBody),
//     }

//     const response = await fetch(fetchUrl, requestOptions)
//     const elapsed = Date.now() - startTime
//     if (!response.ok) await handleApiError(response, fetchUrl, requestBody, elapsed)

//     const data = await response.json()
//     // TEMPORARILY Weight businesses with images
//     const businessesByHasProfileImage = _.groupBy(data?.value, business => Boolean(business?.profileImage))
//     if (!businessesByHasProfileImage.true || !businessesByHasProfileImage.false)
//       return data?.value

//     const orderedBusinesses = _.take([...businessesByHasProfileImage.true, ...businessesByHasProfileImage.false], 10)
//     return orderedBusinesses
// //   } catch (error) {
// logDataByLevel({
//   level: 'error',
//   message: `${error.message}: searchRegisteredBusinesses`,
//   data: error,
// })
//     throw error
//   }
// }

// export async function getBusinessSubmissionsByEmail(
//   requestBody,
// ) {
//   if (!requestBody) return []
//   const startTime = Date.now()
//   try {
//     const fetchUrl = `${process.env.NEXT_PUBLIC_CITYSPARK_API_HOST}/api/business/submissionSearch`
//     const requestOptions = {
//       method: 'POST',
//       headers: {'Content-Type': 'application/json'},
//       body: JSON.stringify(requestBody),
//     }

//     const response = await fetch(fetchUrl, requestOptions)
//     const elapsed = Date.now() - startTime
//     if (!response.ok) await handleApiError(response, fetchUrl, requestBody, elapsed)

//     const data = await response.json()
//     if (!data?.value || data.value.length <= 0) return []

//     const businessSubmissionsByPID = _.groupBy(data.value, 'core.pid')
//     if (_.every(_.keys(businessSubmissionsByPID), key => key === 'undefined')) return []

//     // Only bring latest submissions forward
//     let latestBusinessSubmissions = []
//     _.forEach(businessSubmissionsByPID, (businessSubmissions, pid) => {
//       const latestBusinessSubmission = _.first(businessSubmissions)
//       if (latestBusinessSubmission.state.status <= -1) return

//       latestBusinessSubmissions.push({
//         ...latestBusinessSubmission,
//         corePID: pid,
//       })
//     })

//     return latestBusinessSubmissions || []
// //   } catch (error) {
// logDataByLevel({
//   level: 'error',
//   message: `${error.message}: getBusinessSubmissionsByEmail`,
//   data: error,
// })
//     throw error
//   }
// }

// export async function getRegistrationInfo(
//   businessId,
//   ballotId,
//   customerId,
// ) {
//   if (!businessId && (!ballotId || !customerId)) return null
//   const startTime = Date.now()
//   try {
//     const fetchUrl = `${process.env.NEXT_PUBLIC_CITYSPARK_API_HOST}/api/business/registrationInfo`
//     let requestBody = {pid: businessId}
//     if (ballotId) {
//       requestBody['ballotId'] = ballotId
//     }
//     if (customerId) {
//       requestBody['clientId'] = customerId
//     }
//     let requestOptions = {
//       method: 'POST',
//       headers: {'Content-Type': 'application/json'},
//       body: JSON.stringify(requestBody),
//     }
//     const response = await fetch(fetchUrl, requestOptions)
//     const elapsed = Date.now() - startTime

//     if (!response.ok) await handleApiError(response, fetchUrl, requestBody, elapsed)

//     const data = await response.json()
//     return data.value
// //   } catch (error) {
// logDataByLevel({
//   level: 'error',
//   message: `${error.message}: getRegistrationInfo`,
//   data: error,
// })
//     throw error
//   }
// }

// export async function registerBusiness(
//   requestBody,
// ) {
//   if (_.isEmpty(requestBody)) return null
//   const startTime = Date.now()
//   try {
//     const fetchUrl = `${process.env.NEXT_PUBLIC_CITYSPARK_API_HOST}/api/business/registrationSubmit`
//     const requestOptions = {
//       method: 'POST',
//       headers: {'Content-Type': 'application/json'},
//       body: JSON.stringify(requestBody),
//     }

//     const response = await fetch(fetchUrl, requestOptions)
//     const elapsed = Date.now() - startTime
//     if (!response.ok) await handleApiError(response, fetchUrl, requestBody, elapsed)

//     const data = await response.json()
//     return data
//     // Sample success data
//     // {
//     //   "value": 16133,
//     //   "errorMessage": null,
//     //   "success": true,
//     //   "userOkErrorMsg": false
//     // }
// //   } catch (error) {
// logDataByLevel({
//   level: 'error',
//   message: `${error.message}: registerBusiness`,
//   data: error,
// })
//     throw error
//   }
// }

// export async function updateBusinessSubmissionStatus(
//   requestBody,
// ) {
//   if (_.isEmpty(requestBody)) return null
//   const startTime = Date.now()
//   try {
//     const fetchUrl = `${process.env.NEXT_PUBLIC_CITYSPARK_API_HOST}/api/business/updateStatus`
//     const requestOptions = {
//       method: 'POST',
//       headers: {'Content-Type': 'application/json'},
//       body: JSON.stringify(requestBody),
//     }

//     const response = await fetch(fetchUrl, requestOptions)
//     const elapsed = Date.now() - startTime
//     if (!response.ok) await handleApiError(response, fetchUrl, requestBody, elapsed)

//     const data = await response.json()
//     return data
// //   } catch (error) {
// logDataByLevel({
//   level: 'error',
//   message: `${error.message}: updateBusinessSubmissionStatus`,
//   data: error,
// })
//     throw error
//   }
// }

// export async function getPossibleCities(
//   requestBody,
// ) {
//   if (_.isEmpty(requestBody)) return null
//   const startTime = Date.now()
//   try {
//     const fetchUrl = `${process.env.NEXT_PUBLIC_CITYSPARK_API_HOST}/api/business/PossibleCities`
//     const requestOptions = {
//       method: 'POST',
//       headers: {'Content-Type': 'application/json'},
//       body: JSON.stringify(requestBody),
//     }

//     const response = await fetch(fetchUrl, requestOptions)
//     const elapsed = Date.now() - startTime
//     if (!response.ok) await handleApiError(response, fetchUrl, requestBody, elapsed)

//     const data = await response.json()
//     return data
// //   } catch (error) {
// logDataByLevel({
//   level: 'error',
//   message: `${error.message}: getPossibleCities`,
//   data: error,
// })
//     throw error
//   }
// }

// export async function isVerificationCodeValidForBusinessRegistration(
//   payload,
// ) {
//   if (!payload?.email || !payload.code || !payload.ballot_id) return null
//   const startTime = Date.now()
//   try {
//     const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/verification_codes/isVerificationCodeValidForBusinessRegistration`
//     const requestBody = {
//       email: payload.email,
//       code: payload.code.trim(),
//       ballot_id: payload.ballot_id,
//     }
//     const requestOptions = {
//       method: 'POST',
//       headers: {'Content-Type': 'application/json'},
//       body: JSON.stringify(requestBody),
//     }

//     const response = await fetch(fetchUrl, requestOptions)
//     const elapsed = Date.now() - startTime
//     if (!response.ok) await handleApiError(response, fetchUrl, requestBody, elapsed)

//     const data = await response.json()
//     return data
// //   } catch (error) {
// logDataByLevel({
//   level: 'error',
//   message: `${error.message}: isVerificationCodeValidForBusinessRegistration`,
//   data: error,
// })
//     throw error
//   }
// }

// export async function updateBallotPageBuildingBlocks(
//   ballotId,
//   blocks,
// ) {
//   if (!ballotId || _.isEmpty(blocks)) return null
//   const startTime = Date.now()
//   try {
//     const fetchUrl = `${process.env.NEXT_PUBLIC_BALLOT_API_HOST}/ballots/updateBallotPageBuildingBlocks`
//     const requestBody = {
//       ballot_id: ballotId,
//       ballot_page_building_blocks: blocks,
//     }
//     const requestOptions = {
//       method: 'POST',
//       headers: {'Content-Type': 'application/json'},
//       body: JSON.stringify(requestBody),
//     }

//     const response = await fetch(fetchUrl, requestOptions)
//     const elapsed = Date.now() - startTime
//     if (!response.ok) await handleApiError(response, fetchUrl, requestBody, elapsed)

//     const data = await response.json()
//     return data
// //   } catch (error) {
// logDataByLevel({
//   level: 'error',
//   message: `${error.message}: updateBallotPageBuildingBlocks`,
//   data: error,
// })
//     throw error
//   }
// }
